/* ─────────────────────────────────────────────────────────────────────────────
   GrowthOS — Marketing / Funnel. Replicates the original GrowthOS page
   (frontend/src/app/(dashboard)/analytics/marketing/page.tsx): KPI grid, funnel
   trend, Pipeline scoreboard, D2C Freight four-stage funnel, Platform performance,
   Sales POC-wise scoreboard, Closed Won Deals, Sales POC & pipeline-wise closure %.
   Deal metrics = marketing-sourced deals; leads = marketing lead scope; ProShip
   actuals are limited to marketing merchants (agreed scope).
   ──────────────────────────────────────────────────────────────────────────── */
import React, { useState } from 'react'
import {
  Box, MenuItem, Select, TextField, Tooltip, Typography,
} from '@mui/material'
import {
  CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis,
} from 'recharts'
import CampaignIcon from '@mui/icons-material/Campaign'
import GroupsIcon from '@mui/icons-material/Groups'
import GpsFixedIcon from '@mui/icons-material/GpsFixed'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import PercentIcon from '@mui/icons-material/Percent'
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import StorageIcon from '@mui/icons-material/Storage'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { useGetMarketingFunnelQuery } from '../../redux/growthosMarketingFunnelApiSlice'
import { useListIngestRunsQuery } from '../../redux/growthosConnectorsApiSlice'
import {
  GX_BORDER, GX_COLORS, GX_INK_500, GX_INK_700, GX_INK_900, GX_PAGE_SX,
  GxKpiCard, GxPill, GxSyncStatusBar,
} from '../growthos-shared/GrowthOsUi'
import { comparison, currency, integer, monthLabel, percentage } from './mfFormat'
import {
  Card, ChartTip, ClosedWonDeals, D2CFunnel, PipelineScoreboard, PlatformPerformance,
  PocPipelineScoreboard, PocScoreboard, ScopeTag, SectionTitle,
} from './MfSections'

const PERIODS = [
  { value: 'mtd', label: 'MTD' }, { value: 'last_month', label: 'Last Month' },
  { value: 'last_3m', label: 'Last 3M' }, { value: 'last_6m', label: 'Last 6M' },
  { value: 'ytd', label: 'YTD' }, { value: 'all', label: 'All time' }, { value: 'custom', label: 'Custom' },
]

// Accent order per row matches the original grid: indigo · teal · amber · rose.
const ACCENTS = [GX_COLORS.indigo, GX_COLORS.teal, GX_COLORS.amber, GX_COLORS.pink]

function Kpi({ label, value, sub, icon, accent, definition }) {
  return (
    <Tooltip title={definition || ''} arrow placement="top">
      <Box sx={{ display: 'flex' }}>
        <GxKpiCard label={label} value={value} sub={sub} icon={icon} iconColor={accent} />
      </Box>
    </Tooltip>
  )
}

const metricOf = (m) => (m ? { value: m.value, previous: m.previous, changePct: m.changePct } : null)

export default function MarketingFunnelPage() {
  const [period, setPeriod] = useState('mtd')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const customReady = period !== 'custom' || Boolean(customFrom && customTo)

  const { data, error, isLoading } = useGetMarketingFunnelQuery(
    { period, from: period === 'custom' ? customFrom : undefined, to: period === 'custom' ? customTo : undefined },
    { skip: !customReady },
  )
  const { data: runsData } = useListIngestRunsQuery()
  const k = data?.kpis
  const windowLabel = data?.windowDetail?.label || data?.window?.label || ''
  const trend = (data?.trend || []).map((r) => ({ ...r, name: monthLabel(r.month) }))
  const trailingLabel = trend.length === 12 ? `${monthLabel(trend[0].month)} – ${monthLabel(trend[11].month)}` : undefined

  const budgetMetric = k ? { value: k.marketingBudget, previous: k.marketingBudgetPrevious, changePct: k.marketingBudgetChangePct } : null
  const v = (fn) => (k ? fn() : '—')

  return (
    <Box sx={GX_PAGE_SX}>
      <GxSyncStatusBar runs={runsData?.runs || []} />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 3 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
            <CampaignIcon sx={{ fontSize: 14, color: GX_COLORS.indigo }} />
            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', color: GX_COLORS.indigo }}>Marketing-sourced · all pipelines</Typography>
          </Box>
          <Typography sx={{ fontSize: 26, fontWeight: 700, color: GX_INK_900 }}>Marketing / Funnel</Typography>
          <Typography sx={{ fontSize: 13, color: GX_INK_500, mt: 0.5, maxWidth: 760 }}>
            Leads = contacts created with Lead Source = Marketing (by sub-category) → opportunities &amp; customers = marketing-sourced deals, bifurcated by pipeline.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Select size="small" value={period} onChange={(e) => setPeriod(e.target.value)} sx={{ minWidth: 180, fontSize: 13, bgcolor: '#fff' }}>
            {PERIODS.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
          </Select>
          {period === 'custom' && (
            <>
              <TextField size="small" type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
              <Typography sx={{ color: GX_INK_500 }}>→</Typography>
              <TextField size="small" type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
            </>
          )}
          {windowLabel && <GxPill color={GX_COLORS.indigo}>{windowLabel}</GxPill>}
        </Box>
      </Box>

      {error && (
        <Box sx={{ mb: 2, p: 2, borderRadius: '16px', border: '1px solid #fecdd3', bgcolor: '#fff1f2', fontSize: 13, color: '#be123c' }}>
          Failed to load — {error?.data?.message || error?.message || `HTTP ${error?.status}`}
        </Box>
      )}
      {period === 'custom' && !customReady && (
        <Box sx={{ mb: 2, p: 2, borderRadius: '16px', border: '1px solid #fde68a', bgcolor: '#fffbeb', fontSize: 13, color: '#b45309' }}>
          Pick a start and end date to load the custom range.
        </Box>
      )}

      {/* ── Total snapshot ─────────────────────────────────────────────────── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', xl: 'repeat(4, 1fr)' }, gap: 2, mb: 2 }}>
        <Kpi label="Leads" value={v(() => integer(k.leads.value))} sub={k ? comparison(metricOf(k.leads), integer) : undefined} accent={ACCENTS[0]} icon={<GroupsIcon sx={{ fontSize: 18 }} />}
          definition="Contacts created in the window with Lead Source = Marketing, grouped by Lead Source (Sub Category). ABM leads count on reply date." />
        <Kpi label="Opportunities" value={v(() => integer(k.opportunities.value))} sub={k ? comparison(metricOf(k.opportunities), integer) : undefined} accent={ACCENTS[1]} icon={<GpsFixedIcon sx={{ fontSize: 18 }} />}
          definition="Deals created in the window with Deal Source = Marketing (opportunity = deal)." />
        <Kpi label="Customers · Closed" value={v(() => integer(k.customersClosed.value))} sub={k ? comparison(metricOf(k.customersClosed), integer) : undefined} accent={ACCENTS[2]} icon={<EmojiEventsIcon sx={{ fontSize: 18 }} />}
          definition="Marketing deals that became customers (WON/ONBOARDED) with CLOSE date in the window — regardless of when the deal was created." />
        <Kpi label="Customers · New" value={v(() => integer(k.customersNew.value))} sub={k ? comparison(metricOf(k.customersNew), integer) : undefined} accent={ACCENTS[3]} icon={<AutoAwesomeIcon sx={{ fontSize: 18 }} />}
          definition="Subset of Customers · Closed whose deal was also CREATED within the same window (created & closed in-period)." />
        <Kpi label="Lead → Opportunity" value={v(() => percentage(k.leadToOpportunityPct.value))} sub={k ? comparison(metricOf(k.leadToOpportunityPct), percentage) : undefined} accent={ACCENTS[0]} icon={<PercentIcon sx={{ fontSize: 18 }} />}
          definition="Marketing deals created ÷ marketing leads, same window." />
        <Kpi label="Opportunity → Customer" value={v(() => percentage(k.opportunityToCustomerPct.value))} sub={k ? comparison(metricOf(k.opportunityToCustomerPct), percentage) : undefined} accent={ACCENTS[1]} icon={<PercentIcon sx={{ fontSize: 18 }} />}
          definition="Customers · Closed ÷ opportunities, same window." />
        <Kpi label="Lead → Customer" value={v(() => percentage(k.leadToCustomerPct.value))} sub={k ? comparison(metricOf(k.leadToCustomerPct), percentage) : undefined} accent={ACCENTS[2]} icon={<PercentIcon sx={{ fontSize: 18 }} />}
          definition="Customers · Closed ÷ leads, same window." />
        <Kpi label="Marketing Budget" value={v(() => currency(k.marketingBudget))} sub={budgetMetric ? comparison(budgetMetric, currency) : undefined} accent={ACCENTS[3]} icon={<CurrencyRupeeIcon sx={{ fontSize: 18 }} />}
          definition="Month × sub-category budget (Marketing Budget page) for months in the window, prorated on partial months. Shows — until entered." />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', xl: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <Kpi label="Cost per Lead" value={v(() => currency(k.costPerLead))} sub="Budget ÷ leads (window)" accent={ACCENTS[0]} icon={<CurrencyRupeeIcon sx={{ fontSize: 18 }} />}
          definition={`Prorated marketing budget over the window ÷ leads created in the window. Previous: ${k ? currency(k.costPerLeadPrevious) : '—'}.`} />
        <Kpi label="Cost per Opportunity" value={v(() => currency(k.costPerOpportunity))} sub="Budget ÷ opportunities (window)" accent={ACCENTS[1]} icon={<CurrencyRupeeIcon sx={{ fontSize: 18 }} />}
          definition={`Prorated marketing budget over the window ÷ marketing deals created in the window. Previous: ${k ? currency(k.costPerOpportunityPrevious) : '—'}.`} />
        <Kpi label="Cost per Customer" value={v(() => currency(k.costPerCustomer))} sub="Budget ÷ Customers · Closed (window)" accent={ACCENTS[2]} icon={<CurrencyRupeeIcon sx={{ fontSize: 18 }} />}
          definition={`Prorated marketing budget ÷ Customers · Closed (close-date in window). The per-channel 'Cost per Converted Customer' below uses a DIFFERENT denominator. Previous: ${k ? currency(k.costPerCustomerPrevious) : '—'}.`} />
        <Kpi label="Avg Sales Cycle" value={v(() => (k.avgSalesCycleDays == null ? '—' : `${integer(k.avgSalesCycleDays)} days`))}
          sub={k?.avgSalesCycleDaysPrevious != null ? `Previous: ${integer(k.avgSalesCycleDaysPrevious)} days` : 'created → closed'} accent={ACCENTS[3]} icon={<GpsFixedIcon sx={{ fontSize: 18 }} />}
          definition="Mean created→closed days over the Customers · Closed cohort (won deals closed in this window)." />
      </Box>

      {!isLoading && data && data.hasData === false && (
        <Box sx={{ mb: 3, p: 4, borderRadius: '16px', border: `1px dashed ${GX_BORDER}`, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <StorageIcon sx={{ fontSize: 24, color: GX_COLORS.indigo }} />
          <Typography sx={{ fontWeight: 700, color: GX_INK_700 }}>No marketing-sourced funnel data in this window</Typography>
          <Typography sx={{ fontSize: 13, color: GX_INK_500 }}>Confirm the HubSpot sync captured Lead/Deal Source. Budget metrics appear after month × sub-category budgets are entered.</Typography>
        </Box>
      )}

      {/* ── Marketing funnel trend — trailing 12 months ───────────────────── */}
      <Card sx={{ p: 2.5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
              <SectionTitle icon={<TrendingUpIcon sx={{ fontSize: 18 }} />}>Marketing funnel trend</SectionTitle>
              {trailingLabel && <ScopeTag label={`Chart: Trailing 12mo ${trailingLabel}`} title="The chart is always the last 12 calendar months, independent of the period filter above." />}
              {windowLabel && <ScopeTag label={`Tiles: Selected ${windowLabel}`} title="These three tiles follow the period filter above; only the chart stays trailing-12." />}
            </Box>
            <Typography sx={{ mt: 0.5, fontSize: 12, color: GX_INK_500 }}>Last 12 monthly checkpoints — leads, opportunities and customers closed.</Typography>
          </Box>
          {k && (
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              {[['Leads', k.leads.value], ['Opps', k.opportunities.value], ['Customers · Closed', k.customersClosed.value]].map(([label, val]) => (
                <Box key={label} sx={{ border: `1px solid ${GX_BORDER}`, borderRadius: '12px', p: 1.5, background: 'linear-gradient(160deg,#ffffff 0%,#f8fafc 100%)' }}>
                  <Typography sx={{ fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.12em', color: GX_INK_500 }}>{label}</Typography>
                  <Typography sx={{ fontSize: 20, fontWeight: 700, color: GX_INK_900 }}>{integer(val)}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
        <Box sx={{ mt: 2, height: 288 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trend} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef0f6" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} />
              <RTooltip content={<ChartTip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="leads" name="Leads" stroke="#4f46e5" strokeWidth={2.5} dot={false} />
              <Line type="monotone" dataKey="opportunities" name="Opportunities" stroke="#0d9488" strokeWidth={2.5} dot={false} />
              <Line type="monotone" dataKey="customersClosed" name="Customers · Closed" stroke="#16a34a" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Card>

      <PipelineScoreboard pipelines={data?.pipelines || []} windowLabel={windowLabel} />

      {isLoading && (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>Loading funnel, platform performance &amp; scoreboards…</Typography>
        </Card>
      )}
      {data?.d2c && <D2CFunnel data={data} />}
      {data && <PlatformPerformance platforms={data.platforms || []} />}
      {data?.pocScoreboard && <PocScoreboard board={data.pocScoreboard} windowLabel={windowLabel} />}
      {data && <ClosedWonDeals deals={data.closedWonDeals || []} windowLabel={windowLabel} />}
      {data && <PocPipelineScoreboard rows={data.pocPipelineScoreboard || []} windowLabel={windowLabel} />}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 12, color: GX_INK_500 }}>
        <ArrowForwardIcon sx={{ fontSize: 13 }} /> Conversions are window-mode. Customers · Closed counts by close date; Customers · New requires create &amp; close in the same window.
      </Box>
    </Box>
  )
}
